import { type FC, lazy, Suspense, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMe } from "../hooks/useMe";

// CS 専用の「ログインリンク再送」UI は代理ログイン中だけ dynamic import する。
// 一般ユーザのメインバンドルには載せない (難読化。保護はサーバ側 requireImpersonation)。
const CsLoginLinkResend = lazy(() => import("./CsLoginLinkResend"));
// 同じく CS 専用の「この注文をキャンセル」。注文の画面を開いているときだけ自分で出る。
const CsOrderCancel = lazy(() => import("./CsOrderCancel"));

type Props = {
  showLogout?: boolean;
};

/**
 * 退出時に portal の代理ログイン入口に戻すための URL。
 * dev (vite) では同 origin (`http://localhost:8000`) を相対パスで踏むだけで portal にも届く。
 * 本番は別 host (`portal.survaq.com`) なので絶対 URL が必要。
 */
const PORTAL_IMPERSONATE_URL = import.meta.env.DEV
  ? "/portal/externals/mypage-impersonate"
  : "https://portal.survaq.com/portal/externals/mypage-impersonate";

export const SiteHeader: FC<Props> = ({ showLogout = true }) => {
  const { state, logout } = useMe();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const actingAs = state.me?.actingAs ?? null;

  const handleLogout = async () => {
    setSubmitting(true);
    try {
      await logout();
      // impersonation 中の退出は CS が次の顧客対応に向かうケースが多いので portal の入口に戻す。
      if (actingAs) {
        window.location.href = PORTAL_IMPERSONATE_URL;
        return;
      }
      navigate("/login", { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <header data-print-hide className="border-neutral-200 border-b bg-white">
      {actingAs && (
        <div className="bg-amber-100 text-amber-900 text-xs">
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-2.5 px-5 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-0.5">
              <span className="inline-block rounded bg-amber-200 px-1.5 py-0.5 font-semibold">
                代理ログイン中
              </span>
              <p className="leading-relaxed">
                お客様 <span className="break-all font-medium">{state.me?.email}</span>{" "}
                のマイページを操作しています
              </p>
              <p className="break-all text-amber-800/80">担当: {actingAs.adminEmail}</p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2 self-start sm:self-auto">
              <Suspense fallback={null}>
                <CsOrderCancel />
                <CsLoginLinkResend />
              </Suspense>
              <button
                type="button"
                onClick={handleLogout}
                disabled={submitting}
                className="rounded-md border border-amber-400 px-3 py-1.5 font-medium hover:bg-amber-200 disabled:opacity-50"
              >
                退出
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mx-auto flex w-full max-w-2xl items-center justify-between px-5 py-4">
        <Link to="/" className="flex items-center gap-2 hover:opacity-70">
          <img src="/logo.png" alt="SurvaQ" className="h-7 w-auto" />
          <span className="mt-2 text-neutral-500 text-xs">マイページ</span>
        </Link>
        {/* impersonation 中は通常のログアウトボタンを隠す (バナーの「退出」に統合)。 */}
        {showLogout && state.me && !actingAs && (
          <button
            type="button"
            onClick={handleLogout}
            disabled={submitting}
            className="text-neutral-500 text-sm underline-offset-4 hover:text-neutral-900 hover:underline disabled:opacity-50"
          >
            ログアウト
          </button>
        )}
      </div>
    </header>
  );
};
